import React, { useState, useEffect } from 'react'
import { useReservationsAdmin, type Reserva, type ReservasFiltros } from '../../hooks/useReservationsAdmin'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import ReservaDetailModal from '../../components/admin/ReservaDetailModal'
import DTEReservasModal from '../../components/admin/DTEReservasModal'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../context/AuthContext'
import { getStatusColors } from '../../utils/statusColorMap'
import { ApiClient } from '../../services/api'
import { parseLocalDateString, calcularUnidades } from '../../utils/dateHelpers'
import { formatCurrency } from '../../utils/currencyHelpers'

const ReservationsAdmin: React.FC = () => {
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const { tienePermiso } = useAuth()
  const [paginaActual, setPaginaActual] = useState(1)
  const ITEMS_POR_PAGINA = 15

  const [filtros, setFiltros] = useState<ReservasFiltros>({
    limit: ITEMS_POR_PAGINA,
    offset: 0,
  })

  const [filtroEstado, setFiltroEstado] = useState<string>('todas')
  const [filtroEstadoPago, setFiltroEstadoPago] = useState<string>('todas')
  const [filtroTexto, setFiltroTexto] = useState<string>('')
  const [fechaDesde, setFechaDesde] = useState<string>('')
  const [fechaHasta, setFechaHasta] = useState<string>('')

  // Modal state - Detalle de reserva
  const [selectedReservaId, setSelectedReservaId] = useState<string | undefined>()
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Modal state - DTEs
  const [isShowDTEModal, setIsShowDTEModal] = useState(false)
  const [selectedReservaForDTE, setSelectedReservaForDTE] = useState<string | null>(null)
  const [selectedPagoForDTE, setSelectedPagoForDTE] = useState<number | null>(null)
  const [selectedMontoForDTE, setSelectedMontoForDTE] = useState(0)

  // Estado para expandir/colapsar reservas con múltiples espacios
  const [expandedReservas, setExpandedReservas] = useState<Set<string>>(new Set())

  // Fetch reservas del backend
  const { data: responseData, isLoading, isError, error } = useReservationsAdmin(filtros)
  const reservas = responseData?.reservas || []
  const totalReservas = responseData?.total || 0

  // State para estadísticas
  const [stats, setStats] = useState({
    total_ingresos: 0,
    total_reservas: 0,
    reservas_confirmadas: 0,
    reservas_canceladas: 0,
    reservas_pendientes: 0,
  })
  const [statsLoading, setStatsLoading] = useState(false)

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setPaginaActual(1)
  }, [filtroEstado, filtroEstadoPago, filtroTexto, fechaDesde, fechaHasta])

  // Actualizar filtros cuando cambian
  useEffect(() => {
    const nuevosFiltros: ReservasFiltros = {
      limit: ITEMS_POR_PAGINA,
      offset: (paginaActual - 1) * ITEMS_POR_PAGINA,
    }

    if (filtroEstado !== 'todas') {
      nuevosFiltros.estado = filtroEstado
    }

    if (filtroEstadoPago !== 'todas') {
      nuevosFiltros.estado_pago = filtroEstadoPago
    }

    if (filtroTexto) {
      nuevosFiltros.buscar = filtroTexto
    }

    if (fechaDesde) {
      nuevosFiltros.fecha_desde = fechaDesde
    }

    if (fechaHasta) {
      nuevosFiltros.fecha_hasta = fechaHasta
    }

    setFiltros(nuevosFiltros)
  }, [filtroEstado, filtroEstadoPago, filtroTexto, fechaDesde, fechaHasta, paginaActual])

  // Obtener estadísticas del backend
  useEffect(() => {
    const obtenerStats = async () => {
      setStatsLoading(true)
      try {
        const params = new URLSearchParams()

        if (filtroEstado !== 'todas') {
          params.append('estado', filtroEstado)
        }

        if (fechaDesde) {
          params.append('fecha_desde', fechaDesde)
        }

        if (fechaHasta) {
          params.append('fecha_hasta', fechaHasta)
        }

        const data = await ApiClient.get<typeof stats>(
          `/api/admin-protected/reservas/stats/resumen?${params.toString()}`
        )

        setStats(data)
      } catch (error) {
        console.error('Error obteniendo estadísticas:', error)
      } finally {
        setStatsLoading(false)
      }
    }

    obtenerStats()
  }, [filtroEstado, fechaDesde, fechaHasta])

  const getEstadoColor = (estado: string) => {
    // Map reservation estado values to status types
    let statusType: 'confirmed' | 'completed' | 'cancelled' | 'pending' = 'pending'

    switch (estado) {
      case 'confirmada':
        statusType = 'confirmed'
        break
      case 'completada':
        statusType = 'completed'
        break
      case 'cancelada':
        statusType = 'cancelled'
        break
      case 'en_proceso':
      case 'pendiente':
        statusType = 'pending'
        break
    }

    const statusColors = getStatusColors(statusType, colors)
    return {
      backgroundColor: statusColors.backgroundColor,
      color: statusColors.textColor,
      borderColor: statusColors.borderColor
    }
  }

  const getEstadoPagoColor = (estado: string) => {
    // Map payment estado values to status types
    let statusType: 'confirmed' | 'pending' | 'cancelled' = 'pending'

    switch (estado) {
      case 'pagado':
        statusType = 'confirmed'
        break
      case 'reembolsado':
        statusType = 'cancelled'
        break
      case 'pendiente':
        statusType = 'pending'
        break
    }

    const statusColors = getStatusColors(statusType, colors)
    return {
      backgroundColor: statusColors.backgroundColor,
      color: statusColors.textColor,
      borderColor: statusColors.borderColor
    }
  }

  const limpiarFiltros = () => {
    setFiltroEstado('todas')
    setFiltroEstadoPago('todas')
    setFiltroTexto('')
    setFechaDesde('')
    setFechaHasta('')
  }

  // Toggle para expandir/colapsar reserva con múltiples espacios
  const toggleExpandReserva = (reservaId: string) => {
    const newExpanded = new Set(expandedReservas)
    if (newExpanded.has(reservaId)) {
      newExpanded.delete(reservaId)
    } else {
      newExpanded.add(reservaId)
    }
    setExpandedReservas(newExpanded)
  }


  // Abrir modal de DTEs para una reserva
  const handleOpenDTEsModal = (reservaId: string, monto: number) => {
    setSelectedReservaForDTE(reservaId)
    setSelectedMontoForDTE(monto)

    // Obtener el primer pago (aprobado) de la reserva
    const obtenerPagoDeReserva = async () => {
      try {
        const pagos = await ApiClient.get<any[]>(
          `/api/payments?reserva_id=${reservaId}`
        )

        if (pagos && pagos.length > 0) {
          // Usar el primer pago aprobado
          const pagoAprobado = pagos.find(p => p.estado === 'aprobado') || pagos[0]
          setSelectedPagoForDTE(pagoAprobado.id)
        }
      } catch (error) {
        console.error('Error obteniendo pagos:', error)
      }
    }

    obtenerPagoDeReserva()
    setIsShowDTEModal(true)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestión de Reservas
            </h1>
          </div>
          {tienePermiso('reservas.manual') && (
            <button
              onClick={() => window.location.href = '/admin/reservas/crear-manual'}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-300 ease-out cursor-pointer"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary
              }}
            >
              + Crear Reserva
            </button>
          )}
        </div>
        {isLoading && (
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
            <span>Cargando reservas...</span>
          </div>
        )}
        {isError && (
          <div className="flex items-center space-x-2 text-sm mb-6" style={{ color: colors.primaryDark }}>
            <span>❌ Error al cargar reservas</span>
          </div>
        )}

        {/* Stats */}
        <div className={`grid gap-6 mb-8 ${
          deviceInfo.isMobile
            ? 'grid-cols-1'
            : deviceInfo.isTablet
            ? 'grid-cols-2'
            : tienePermiso('pagos.ver')
            ? 'grid-cols-4'
            : 'grid-cols-3'
        }`}>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Reservas</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_reservas}</p>
              </div>
              <div className="text-4xl">📅</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Confirmadas</p>
                <p className="text-3xl font-bold" style={{ color: colors.primary }}>
                  {stats.reservas_confirmadas}
                </p>
              </div>
              <div className="text-4xl">✅</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Canceladas</p>
                <p className="text-3xl font-bold" style={{ color: colors.primaryDark }}>
                  {stats.reservas_canceladas}
                </p>
              </div>
              <div className="text-4xl">❌</div>
            </div>
          </div>

          {tienePermiso('pagos.ver') && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Ingresos</p>
                  <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                    ${stats.total_ingresos.toLocaleString()}
                  </p>
                </div>
                <div className="text-4xl">💰</div>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
            {(filtroEstado !== 'todas' || filtroEstadoPago !== 'todas' || filtroTexto || fechaDesde || fechaHasta) && (
              <button
                onClick={limpiarFiltros}
                className="text-xs sm:text-sm font-medium" style={{ color: colors.primary }}
              >
                Limpiar
              </button>
            )}
          </div>

          {/* Búsqueda general */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🔍 Buscar
            </label>
            <input
              type="text"
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              placeholder="Cliente, email, código..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
            />
          </div>

          {/* Grid de filtros */}
          <div className={`grid gap-3 ${
            deviceInfo.isMobile
              ? 'grid-cols-2'
              : deviceInfo.isTablet
              ? 'grid-cols-3'
              : 'grid-cols-5'
          }`}>
            {/* Filtro por estado de reserva */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="w-full px-2 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              >
                <option value="todas">Todas</option>
                <option value="confirmada">Confirmada</option>
                <option value="en_proceso">En proceso</option>
                <option value="pendiente">Pendiente</option>
                <option value="completada">Completada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            {/* Filtro de estado de pago */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Pago
              </label>
              <select
                value={filtroEstadoPago}
                onChange={(e) => setFiltroEstadoPago(e.target.value)}
                className="w-full px-2 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              >
                <option value="todas">Todas</option>
                <option value="pagado">Pagado</option>
                <option value="pendiente">Pendiente</option>
                <option value="reembolsado">Reembolsado</option>
              </select>
            </div>

            {/* Filtro fecha desde */}
            {!deviceInfo.isMobile && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-2 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              />
            </div>
            )}

            {/* Filtro fecha hasta */}
            {!deviceInfo.isMobile && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-2 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              />
            </div>
            )}

            {/* Indicador de resultados */}
            <div className={`flex items-end ${
              deviceInfo.isMobile
                ? 'col-span-2'
                : 'col-span-1'
            }`}>
              <div className="px-2 sm:px-4 py-2 bg-gray-100 rounded-lg w-full text-center">
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  {reservas.length} result.
                </span>
              </div>
            </div>
          </div>

          {/* Filtros de fecha en mobile */}
          {deviceInfo.isMobile && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                📅 Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                📅 Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              />
            </div>
          </div>
          )}
        </div>

        {/* Table (Desktop) */}
        {deviceInfo.isDesktop && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Espacio
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entrada
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Salida
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unidades
                  </th>
                  {tienePermiso('pagos.ver') && (
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  )}
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pago
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={tienePermiso('pagos.ver') ? 10 : 9} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                        <span>Cargando reservas...</span>
                      </div>
                    </td>
                  </tr>
                ) : reservas.length === 0 ? (
                  <tr>
                    <td colSpan={tienePermiso('pagos.ver') ? 10 : 9} className="px-6 py-12 text-center text-gray-500">
                      No hay reservas con el filtro seleccionado
                    </td>
                  </tr>
                ) : (
                  reservas.map((reserva) => {
                    const tieneMultiplesEspacios = (reserva.espacios?.length || 0) > 1
                    const estaExpandida = expandedReservas.has(reserva.id)

                    return (
                      <React.Fragment key={reserva.id}>
                        {/* Fila principal de la reserva */}
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                              {tieneMultiplesEspacios && (
                                <button
                                  onClick={() => toggleExpandReserva(reserva.id)}
                                  className="text-gray-600 hover:text-gray-900 font-bold"
                                  title={estaExpandida ? 'Colapsar' : 'Expandir'}
                                >
                                  {estaExpandida ? '➖' : '➕'}
                                </button>
                              )}
                              {reserva.id ? reserva.id.substring(0, 8) + '...' : 'N/A'}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-sm font-medium text-gray-900">{reserva.cliente_nombre || reserva.huesped_nombre || 'N/A'}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[150px]">{reserva.cliente_email || reserva.huesped_email || 'N/A'}</div>
                          </td>
                          {tieneMultiplesEspacios ? (
                            // Múltiples espacios: sin fechas en encabezado
                            <>
                              <td colSpan={4} className="px-3 py-3 text-sm text-gray-900">
                                <div className="max-w-[120px] truncate">
                                  {`${reserva.espacios?.length || 0} espacios`}
                                </div>
                              </td>
                            </>
                          ) : (
                            // Un solo espacio: mostrar fechas y unidades
                            <>
                              <td className="px-3 py-3 text-sm text-gray-900">
                                <div className="max-w-[120px] truncate">
                                  {reserva.espacio_nombre || 'N/A'}
                                </div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                                {reserva.espacios?.[0]?.fecha_entrada
                                  ? parseLocalDateString(reserva.espacios[0].fecha_entrada).toLocaleDateString('es-CL')
                                  : reserva.fecha_entrada
                                  ? parseLocalDateString(reserva.fecha_entrada).toLocaleDateString('es-CL')
                                  : 'N/A'}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                                {reserva.espacios?.[0]?.fecha_salida
                                  ? parseLocalDateString(reserva.espacios[0].fecha_salida).toLocaleDateString('es-CL')
                                  : reserva.fecha_salida
                                  ? parseLocalDateString(reserva.fecha_salida).toLocaleDateString('es-CL')
                                  : 'N/A'}
                              </td>
                              <td className="px-2 py-3 text-center text-sm text-gray-900">
                                {(() => {
                                  const espacio = reserva.espacios?.[0]
                                  const fechaEntrada = espacio?.fecha_entrada || reserva.fecha_entrada
                                  const fechaSalida = espacio?.fecha_salida || reserva.fecha_salida
                                  const unidadReserva = espacio?.unidad_reserva || reserva.unidad_reserva
                                  return fechaEntrada && fechaSalida ? calcularUnidades(fechaEntrada, fechaSalida, unidadReserva) : 'N/A'
                                })()}
                              </td>
                            </>
                          )}
                          {tienePermiso('pagos.ver') && (
                            <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                              {reserva.monto_total ? formatCurrency(reserva.monto_total) : 'N/A'}
                            </td>
                          )}
                          <td className="px-3 py-3">
                            <span
                              className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border"
                              style={getEstadoColor(reserva.estado || '')}
                            >
                              {reserva.estado || 'N/A'}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border"
                              style={getEstadoPagoColor(reserva.estado_pago || '')}
                            >
                              {reserva.estado_pago || 'N/A'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500 text-center space-x-3">
                            <button
                              onClick={() => {
                                setSelectedReservaId(reserva.id)
                                setIsModalOpen(true)
                              }}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Ver detalles de la reserva"
                            >
                              🔍
                            </button>
                            {tienePermiso('dtes.ver') && (
                              <button
                                onClick={() => handleOpenDTEsModal(reserva.id, reserva.monto_total || 0)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title="Gestionar DTEs"
                              >
                                📄
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* Filas expandibles para múltiples espacios - CON FECHAS INDIVIDUALES */}
                        {tieneMultiplesEspacios && estaExpandida && reserva.espacios?.map((espacio, idx) => {
                          const fechaEntrada = espacio.fecha_entrada || reserva.fecha_entrada
                          const fechaSalida = espacio.fecha_salida || reserva.fecha_salida
                          const unidadReserva = espacio.unidad_reserva || reserva.unidad_reserva
                          const unidadesEspacio = fechaEntrada && fechaSalida ? calcularUnidades(fechaEntrada, fechaSalida, unidadReserva) : 0

                          return (
                            <tr key={`${reserva.id}-espacio-${idx}`} className="bg-gray-50 border-l-4" style={{ borderLeftColor: colors.primary }}>
                              <td colSpan={2}></td>
                              <td className="px-3 py-3 text-sm text-gray-600">
                                <div className="flex items-center gap-2 ml-4">
                                  <span className="font-medium">{espacio.nombre}</span>
                                  {espacio.codigo && <span className="text-xs text-gray-500">({espacio.codigo})</span>}
                                </div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600">
                                {fechaEntrada ? parseLocalDateString(fechaEntrada).toLocaleDateString('es-CL') : 'N/A'}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600">
                                {fechaSalida ? parseLocalDateString(fechaSalida).toLocaleDateString('es-CL') : 'N/A'}
                              </td>
                              <td className="px-2 py-3 text-center text-sm text-gray-600">
                                {unidadesEspacio || 'N/A'}
                              </td>
                              {tienePermiso('pagos.ver') && (
                                <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                  {espacio.tarifa_unitaria ? formatCurrency(espacio.tarifa_unitaria) : '-'}
                                </td>
                              )}
                              <td colSpan={3} className="px-3 py-3 text-xs text-gray-500 text-center">
                                {/* Detalles adicionales si es necesario */}
                              </td>
                            </tr>
                          )
                        })}
                      </React.Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Cards View (Mobile/Tablet) */}
        {!deviceInfo.isDesktop && (
        <div className="space-y-4">
          {isLoading ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                <span>Cargando reservas...</span>
              </div>
            </div>
          ) : reservas.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
              No hay reservas con el filtro seleccionado
            </div>
          ) : (
            reservas.map((reserva) => (
              <div key={reserva.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {reserva.cliente_nombre || reserva.huesped_nombre || 'N/A'}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      {reserva.cliente_email || reserva.huesped_email || 'N/A'}
                    </p>
                  </div>
                  <div className="ml-2 flex gap-2 flex-shrink-0 text-lg">
                    <button
                      onClick={() => {
                        setSelectedReservaId(reserva.id)
                        setIsModalOpen(true)
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Ver detalles de la reserva"
                    >
                      🔍
                    </button>
                    {tienePermiso('dtes.ver') && (
                      <button
                        onClick={() => handleOpenDTEsModal(reserva.id, reserva.monto_total || 0)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="Gestionar DTEs"
                      >
                        📄
                      </button>
                    )}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Espacio</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{reserva.espacio_nombre || 'N/A'}</p>
                  </div>
                  {tienePermiso('pagos.ver') && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Total</p>
                      <p className="text-sm font-medium text-gray-900">
                        {reserva.monto_total ? formatCurrency(reserva.monto_total) : 'N/A'}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Entrada</p>
                    <p className="text-sm font-medium text-gray-900">
                      {reserva.fecha_entrada ? parseLocalDateString(reserva.fecha_entrada).toLocaleDateString('es-CL') : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Salida</p>
                    <p className="text-sm font-medium text-gray-900">
                      {reserva.fecha_salida ? parseLocalDateString(reserva.fecha_salida).toLocaleDateString('es-CL') : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Unidades</p>
                    <p className="text-sm font-medium text-gray-900">
                      {reserva.fecha_entrada && reserva.fecha_salida
                        ? calcularUnidades(
                            reserva.fecha_entrada,
                            reserva.fecha_salida,
                            reserva.espacios?.[0]?.unidad_reserva || reserva.unidad_reserva
                          )
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>

                {/* Status Badges */}
                <div className="flex gap-2 flex-wrap">
                  <span
                    className="px-2 py-1 text-xs leading-5 font-semibold rounded-full border"
                    style={getEstadoColor(reserva.estado || '')}
                  >
                    {reserva.estado || 'N/A'}
                  </span>
                  <span
                    className="px-2 py-1 text-xs leading-5 font-semibold rounded-full border"
                    style={getEstadoPagoColor(reserva.estado_pago || '')}
                  >
                    {reserva.estado_pago || 'N/A'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
        )}

        {!isLoading && reservas.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay reservas con el filtro seleccionado
          </div>
        )}

        {/* Paginación */}
        {reservas.length > 0 && (
          <div className={`mt-6 border-t border-gray-200 pt-4 ${
            deviceInfo.isMobile ? 'space-y-3' : 'flex items-center justify-between'
          }`}>
            {/* Contador de resultados */}
            <div className={`text-sm text-gray-600 ${deviceInfo.isMobile ? 'text-center' : ''}`}>
              Mostrando {(paginaActual - 1) * ITEMS_POR_PAGINA + 1} a{' '}
              {Math.min(paginaActual * ITEMS_POR_PAGINA, totalReservas)} de {totalReservas} reservas
            </div>

            {/* Botones de paginación */}
            <div className={`flex gap-2 ${deviceInfo.isMobile ? 'justify-center' : ''}`}>
              {/* Botón Anterior */}
              <button
                onClick={() => setPaginaActual(Math.max(1, paginaActual - 1))}
                disabled={paginaActual === 1}
                className={`${
                  deviceInfo.isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
                } font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors`}
                title="Página anterior"
              >
                {deviceInfo.isMobile ? '←' : '← Anterior'}
              </button>

              {/* Números de página */}
              <div className="flex items-center gap-1">
                {Array.from({
                  length: Math.ceil(totalReservas / ITEMS_POR_PAGINA),
                }).map((_, index) => {
                  const pagina = index + 1
                  const esPaginaActual = pagina === paginaActual
                  const totalPaginas = Math.ceil(totalReservas / ITEMS_POR_PAGINA)

                  // ✅ Control inteligente de páginas para mobile Y desktop
                  const maxPaginasVisibles = deviceInfo.isMobile ? 3 : 7 // Mobile: 3, Desktop: 7
                  const rango = Math.floor(maxPaginasVisibles / 2)

                  // Siempre mostrar primera y última
                  const mostrarPrimera = pagina === 1
                  const mostrarUltima = pagina === totalPaginas

                  // Mostrar páginas alrededor de la actual
                  const mostrarAlrededor = Math.abs(pagina - paginaActual) <= rango

                  const showPage = mostrarPrimera || mostrarUltima || mostrarAlrededor

                  if (!showPage) {
                    // Mostrar "..." solo una vez entre grupos
                    if (pagina === paginaActual - rango - 1 || pagina === paginaActual + rango + 1) {
                      return <span key={pagina} className="px-2 text-gray-400">...</span>
                    }
                    return null
                  }

                  return (
                    <button
                      key={pagina}
                      onClick={() => setPaginaActual(pagina)}
                      className={`${
                        deviceInfo.isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
                      } font-medium rounded-lg transition-colors ${
                        esPaginaActual
                          ? 'bg-gray-900 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                      title={`Ir a página ${pagina}`}
                    >
                      {pagina}
                    </button>
                  )
                })}
              </div>

              {/* Botón Siguiente */}
              <button
                onClick={() => {
                  const totalPaginas = Math.ceil(totalReservas / ITEMS_POR_PAGINA)
                  setPaginaActual(Math.min(totalPaginas, paginaActual + 1))
                }}
                disabled={paginaActual >= Math.ceil(totalReservas / ITEMS_POR_PAGINA)}
                className={`${
                  deviceInfo.isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
                } font-medium rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors`}
                title="Página siguiente"
              >
                {deviceInfo.isMobile ? '→' : 'Siguiente →'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal para ver detalles de reserva */}
      <ReservaDetailModal
        reservaId={selectedReservaId}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedReservaId(undefined)
        }}
      />

      {/* Modal para gestionar DTEs de una reserva */}
      <DTEReservasModal
        isOpen={isShowDTEModal}
        onClose={() => {
          setIsShowDTEModal(false)
          setSelectedReservaForDTE(null)
          setSelectedPagoForDTE(null)
          setSelectedMontoForDTE(0)
        }}
        reservaId={selectedReservaForDTE}
      />

    </div>
  )
}

export default ReservationsAdmin
